import queryString from 'query-string';
import * as React from 'react';
import { v4 as uuid } from 'uuid';

import ActivityTableContainer from './activity_table_container';
import FilterColumn from './filter_column';
import Header from './header';
import { Activity, ActivityFocusEditor } from './interfaces';
import MobileFilterMenu from './mobile_filter_menu';
import MobileSortMenu from './mobile_sort_menu';
import { TARGET_GRADE_RANGE_ASCENDING, TARGET_GRADE_RANGE_FILTERS, activityClassificationGroupings, calculateNumberOfPages, filterByFlag, filters, floorBand, lowerBands } from './shared';

import { requestDelete, requestGet, requestPost } from '../../../../../../modules/request/index';
import { ACTIVITY_LIBRARY_SEARCH_LOG_SOURCE, Snackbar, Spinner, defaultSnackbarTimeout, getPageTitle, logSearch } from '../../../../../Shared/index';
import useSnackbarMonitor from '../../../../../Shared/hooks/useSnackbarMonitor';
import useDebounce from '../../../../hooks/useDebounce';

const DEBOUNCE_LENGTH = 500
// Longer than DEBOUNCE_LENGTH so ordinary typing pauses do not log a row per fragment.
const SEARCH_LOG_DEBOUNCE_LENGTH = 1500

interface CustomActivityPackProps {
  passedActivities?: Activity[],
  isStaff?: boolean
  clickContinue: (event: any) => void,
  selectedActivities: Activity[],
  setSelectedActivities: (selectedActivities: Activity[]) => void,
  toggleActivitySelection: (activity: Activity) => void,
  flagset: string,
  activityFocusEditor?: ActivityFocusEditor,
  showEvidenceBanner?: boolean,
  showLessonsBanner?: boolean,
  saveButtonEnabled?: boolean
}

const CustomActivityPack = ({
  passedActivities,
  clickContinue,
  selectedActivities,
  setSelectedActivities,
  toggleActivitySelection,
  isStaff,
  activityFocusEditor,
  showEvidenceBanner,
  showLessonsBanner,
  saveButtonEnabled,
  flagset,
}: CustomActivityPackProps) => {
  document.title = getPageTitle("Assign", "Activity Library")
  const url = queryString.parseUrl(window.location.href, { arrayFormat: 'bracket', parseNumbers: true }).query;

  const [activities, setActivities] = React.useState(passedActivities || [])
  const [filteredActivities, setFilteredActivities] = React.useState(activities)
  const [loading, setLoading] = React.useState(!passedActivities);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [search, setSearch] = React.useState(url.search || '')
  const [filterHistory, setFilterHistory] = React.useState([])
  const [activityClassificationFilters, setActivityClassificationFilters] = React.useState(url.activityClassificationFilters || [])
  const [ellFilters, setELLFilters] = React.useState(url.ellFilters || [])
  const [standardsFilters, setStandardsFilters] = React.useState({ ellFilters: ellFilters })
  const [targetGradeRangeFilters, setTargetGradeRangeFilters] = React.useState(url.targetGradeRangeFilters || [])
  const [activityFocusFilters, setActivityFocusFilters] = React.useState(url.activityFocusFilters || [])
  const [contentPartnerFilters, setContentPartnerFilters] = React.useState(url.contentPartnerFilters || [])
  const [earlyAccessFilters, setEarlyAccessFilters] = React.useState(url.earlyAccessFilters || [])
  const [topicFilters, setTopicFilters] = React.useState(url.topicFilters || [])
  const [typingRequirementFilters, setTypingRequirementFilters] = React.useState(url.typingRequirementFilters || [])
  const [flagFilters, setFlagFilters] = React.useState(url.flagFilters || isStaff ? ['production'] : [])
  const [savedActivityFilters, setSavedActivityFilters] = React.useState([])
  const [showMobileFilterMenu, setShowMobileFilterMenu] = React.useState(false)
  const [showMobileSortMenu, setShowMobileSortMenu] = React.useState(false)
  const [sort, setSort] = React.useState(TARGET_GRADE_RANGE_ASCENDING)
  const [savedActivityIds, setSavedActivityIds] = React.useState([])
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarText, setSnackbarText] = React.useState('')
  const [topics, setTopics] = React.useState([])

  const debouncedSearch = useDebounce(search, DEBOUNCE_LENGTH);
  const debouncedActivityClassificationFilters = useDebounce(activityClassificationFilters, DEBOUNCE_LENGTH);
  const debouncedELLFilters = useDebounce(ellFilters, DEBOUNCE_LENGTH);
  const debouncedTargetGradeRangeFilters = useDebounce(targetGradeRangeFilters, DEBOUNCE_LENGTH);
  const debouncedActivityFocusFilters = useDebounce(activityFocusFilters, DEBOUNCE_LENGTH);
  const debouncedContentPartnerFilters = useDebounce(contentPartnerFilters, DEBOUNCE_LENGTH);
  const debouncedEarlyAccessActivityFilters = useDebounce(earlyAccessFilters, DEBOUNCE_LENGTH);
  const debouncedTopicFilters = useDebounce(topicFilters, DEBOUNCE_LENGTH);
  const debouncedTypingRequirementFilters = useDebounce(typingRequirementFilters, DEBOUNCE_LENGTH);
  const debouncedSavedActivityFilters = useDebounce(savedActivityFilters, DEBOUNCE_LENGTH);
  const debouncedFlagFilters = useDebounce(flagFilters, DEBOUNCE_LENGTH);

  const loggableSearch = useDebounce(search, SEARCH_LOG_DEBOUNCE_LENGTH)
  const searchSessionUid = React.useRef(uuid())
  const hasSearched = React.useRef(false)
  const lastLoggedQuery = React.useRef('')

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  React.useEffect(() => {
    if (loading) { getActivitiesAndTopics() }
    getSavedActivities();
  }, []);

  React.useEffect(() => {
    if (passedActivities) {
      setLoading(!passedActivities.length)
      setActivities(passedActivities)
    }
  }, [passedActivities])

  React.useEffect(() => {
    setStandardsFilters({ ellFilters })
  }, [ellFilters])

  React.useEffect(() => {
    if (activities.length) {
      updateFilteredActivities();
      setLoading(false)
    }
  }, [activities])

  React.useEffect(recordSettledSearch, [loggableSearch])

  React.useEffect(handleFilterChange, [debouncedSearch, debouncedActivityClassificationFilters, debouncedTargetGradeRangeFilters, debouncedELLFilters, debouncedActivityFocusFilters, debouncedContentPartnerFilters, debouncedEarlyAccessActivityFilters, debouncedTopicFilters, debouncedSavedActivityFilters, debouncedFlagFilters, debouncedTypingRequirementFilters])

  function handleFilterChange() {
    updateQueryString()
    updateFilteredActivities()
  }

  function calculateNumberOfFilters() {
    let number = 0
    number += search.length ? 1 : 0
    number += ellFilters.length ? 1 : 0
    number += targetGradeRangeFilters.length
    number += activityFocusFilters.length
    number += contentPartnerFilters.length
    number += earlyAccessFilters.length
    number += topicFilters.length
    number += typingRequirementFilters.length
    number += savedActivityFilters.length ? 1 : 0
    number += flagFilters.length

    activityClassificationGroupings.forEach((g) => {
      if (g.keys.every(key => activityClassificationFilters.includes(key))) {
        number += 1
      } else {
        number += g.keys.filter(key => activityClassificationFilters.includes(key)).length
      }
    })

    return number
  }

  function getActivitiesAndTopics() {
    requestGet('/activities/search',
      (data) => {
        setActivities(data.activities);
        setTopics(data.topics);
      }
    )
  }

  function getSavedActivities(callback=null) {
    requestGet('/teacher_saved_activities/saved_activity_ids_for_current_user',
      (data) => {
        setSavedActivityIds(data.activity_ids);
        callback ? callback() : null
      }
    )
  }

  function saveActivity(activityId) {
    requestPost(
      '/teacher_saved_activities/create_by_activity_id_for_current_user',
      { activity_id: activityId },
      () => {
        const callback = () => {
          setSnackbarText('Activity saved')
          setShowSnackbar(true)
        }
        getSavedActivities(callback)
      }
    )
  }

  function unsaveActivity(activityId) {
    requestDelete(
      '/teacher_saved_activities/destroy_by_activity_id_for_current_user',
      { activity_id: activityId },
      () => {
        const callback = () => {
          setSnackbarText('Activity unsaved')
          setShowSnackbar(true)
        }
        getSavedActivities(callback)
      }
    )
  }

  function handleSearch(newSearchTerm: string) {
    hasSearched.current = true
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setSearch, argument: search }]))
    setSearch(newSearchTerm)
  }

  function handleActivityClassificationFilterChange(newActivityClassificationFilters: string[]) {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setActivityClassificationFilters, argument: activityClassificationFilters }]))
    setActivityClassificationFilters(newActivityClassificationFilters)
  }

  function handleTargetGradeRangeFilterChange(newTargetGradeRangeFilters: string[]) {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setTargetGradeRangeFilters, argument: targetGradeRangeFilters }]))
    setTargetGradeRangeFilters(newTargetGradeRangeFilters)
  }

  function handleELLFilterChange(newELLFilters: string[]) {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setELLFilters, argument: ellFilters }]))
    setELLFilters(newELLFilters)
  }

  function handleActivityFocusFilterChange(newActivityFocusFilters: number[]) {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setActivityFocusFilters, argument: activityFocusFilters }]))
    setActivityFocusFilters(newActivityFocusFilters)
  }

  function handleContentPartnerFilterChange(newContentPartnerFilters: number[]) {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setContentPartnerFilters, argument: contentPartnerFilters }]))
    setContentPartnerFilters(newContentPartnerFilters)
  }

  function handleEarlyAccessFilterChange(newEarlyAccessFilters: string[]) {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setEarlyAccessFilters, argument: earlyAccessFilters }]))
    setEarlyAccessFilters(newEarlyAccessFilters)
  }

  function handleTopicFilterChange(newTopicFilters: number[]) {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setTopicFilters, argument: topicFilters }]))
    setTopicFilters(newTopicFilters)
  }

  function handleFlagFilterChange(newFlagFilters: string[]) {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setFlagFilters, argument: flagFilters }]))
    setFlagFilters(newFlagFilters)
  }

  function handleTypingRequirementFilterChange(newTypingRequirementFilters: string[]) {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setTypingRequirementFilters, argument: typingRequirementFilters }]))
    setTypingRequirementFilters(newTypingRequirementFilters)
  }

  function handleSavedActivityFilterChange() {
    setFilterHistory(prevFilterHistory => prevFilterHistory.concat([{ function: setSavedActivityFilters, argument: savedActivityFilters }]))
    setSavedActivityFilters(savedActivityFilters.length ? [] : savedActivityIds)
  }

  function resetAllFilters() {
    setFilterHistory([])
    setSearch('')
    setActivityClassificationFilters([])
    setTargetGradeRangeFilters([])
    setELLFilters([])
    setActivityFocusFilters([])
    setContentPartnerFilters([])
    setTopicFilters([])
    setSavedActivityFilters([])
    setFlagFilters([])
    setTypingRequirementFilters([])
  }

  function filterActivities(ignoredKey=null) {
    return activities.filter(activity => Object.keys(filters).every(k => {
      if (k === ignoredKey) { return true }
      return filters[k](eval(k), activity)
    }))
  }

  function updateFilteredActivities() {
    if (!activities.length) { return }

    const newFilteredActivities = filterActivities()
    const newNumberOfPages = calculateNumberOfPages(newFilteredActivities)
    if (currentPage > newNumberOfPages && currentPage !== 1) { setCurrentPage(newNumberOfPages) }
    setFilteredActivities(newFilteredActivities)
    const scrollContainer = document.getElementsByClassName('main-content-container')[0]
    scrollContainer && scrollContainer.scrollTo(0, 0)
  }

  function undoLastFilter() {
    const lastIndex = filterHistory.length - 1
    const lastItem = filterHistory[lastIndex]
    lastItem.function(lastItem.argument)
    const newFilterHistory = filterHistory.splice(0, lastIndex)
    setFilterHistory(newFilterHistory)
  }

  function activeFilterQuery({ includeSearch }) {
    const queryHash = {}
    Object.keys(filters).forEach(k => {
      if (k === 'search' && !includeSearch) { return }
      const actualValue = eval(k)
      if (actualValue.length) {
        queryHash[k] = actualValue
      }
    })
    return queryHash
  }

  // Not logged: filter-only changes, the term hydrated from the URL on mount (no one
  // typed it), and anything in the staff activity focus editor (not teacher behavior).
  function recordSettledSearch() {
    if (activityFocusEditor) { return }
    if (!hasSearched.current) { return }

    const query = String(loggableSearch).trim()
    if (!query.length) { return }
    if (query === lastLoggedQuery.current) { return }

    lastLoggedQuery.current = query
    logSearch({
      source: ACTIVITY_LIBRARY_SEARCH_LOG_SOURCE,
      query,
      filters: activeFilterQuery({ includeSearch: false }),
      resultCount: filterActivities().length,
      searchSessionUid: searchSessionUid.current,
    })
  }

  function updateQueryString() {
    const queryHash = activeFilterQuery({ includeSearch: true })
    const newUrl = queryString.stringifyUrl({ url: `${window.location.origin}${window.location.pathname}`, query: queryHash }, { arrayFormat: 'bracket' })
    window.history.pushState({ path: newUrl }, '', newUrl)
  }

  if (loading) {
    return <div className="custom-activity-pack-page loading"><Spinner /></div>
  }

  const filterColumnProps = {
    activities,
    activityFocusFilters,
    activityClassificationFilters,
    calculateNumberOfFilters,
    contentPartnerFilters,
    earlyAccessFilters,
    filterActivities,
    filteredActivities,
    ellFilters,
    targetGradeRangeFilters,
    handleActivityFocusFilterChange,
    handleActivityClassificationFilterChange,
    handleContentPartnerFilterChange,
    handleEarlyAccessFilterChange,
    handleELLFilterChange,
    handleTargetGradeRangeFilterChange,
    resetAllFilters,
    topicFilters,
    handleTopicFilterChange,
    savedActivityFilters,
    handleSavedActivityFilterChange,
    savedActivityIds,
    handleFlagFilterChange,
    flagFilters,
    handleTypingRequirementFilterChange,
    typingRequirementFilters,
    isStaff,
    activityFocusEditor,
    flagset,
    topics,
  }

  const selectedActivitiesFilteredByFlag =  isStaff && !flagFilters.length ? [] : selectedActivities.filter(a => filterByFlag(flagFilters, a))

  // Activities from bands below the lowest selected band, grouped by their floor
  // band in descending order, so each group can surface its own "Additional
  // Activities" callout beneath the on-band results.
  function computeAdditionalActivityGroups() {
    const bands = lowerBands(targetGradeRangeFilters)
    if (!bands.length) { return [] }

    const candidates = filterActivities(TARGET_GRADE_RANGE_FILTERS).filter(activity => {
      const bandNames = activity.grade_band_names || []
      return bandNames.length && !bandNames.some(band => targetGradeRangeFilters.includes(band))
    })

    return bands.slice().reverse()
      .map(band => ({ band, activities: candidates.filter(activity => floorBand(activity.grade_band_names) === band) }))
      .filter(group => group.activities.length)
  }

  const additionalActivityGroups = computeAdditionalActivityGroups()

  return (
    <div className="custom-activity-pack-page">
      <Snackbar text={snackbarText} visible={showSnackbar} />
      <MobileFilterMenu {...filterColumnProps} setShowMobileFilterMenu={setShowMobileFilterMenu} showMobileFilterMenu={showMobileFilterMenu} />
      <MobileSortMenu setShowMobileSortMenu={setShowMobileSortMenu} setSort={setSort} showMobileSortMenu={showMobileSortMenu} />
      <FilterColumn {...filterColumnProps} />
      <main className="main-content-container" id="choose-activities-container">
        <Header
          handleClickContinue={clickContinue}
          isStaff={isStaff}
          saveButtonEnabled={saveButtonEnabled}
          selectedActivities={selectedActivitiesFilteredByFlag}
          setSelectedActivities={setSelectedActivities}
          toggleActivitySelection={toggleActivitySelection}
          topics={topics}
        />
        <ActivityTableContainer
          additionalActivityGroups={additionalActivityGroups}
          currentPage={currentPage}
          filteredActivities={filteredActivities}
          handleSearch={handleSearch}
          resetAllFilters={resetAllFilters}
          saveActivity={saveActivity}
          savedActivityIds={savedActivityIds}
          search={search}
          selectedActivities={selectedActivities}
          setCurrentPage={setCurrentPage}
          setShowMobileFilterMenu={setShowMobileFilterMenu}
          setShowMobileSortMenu={setShowMobileSortMenu}
          setSort={setSort}
          showEvidenceBanner={showEvidenceBanner}
          showLessonsBanner={showLessonsBanner}
          sort={sort}
          toggleActivitySelection={toggleActivitySelection}
          topics={topics}
          undoLastFilter={undoLastFilter}
          unsaveActivity={unsaveActivity}
        />
      </main>
    </div>
  )
}

export default CustomActivityPack
